import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CSRF_KEY } from '@/app/router/authorizeRedirect';
import { postAuthLanding } from '@/features/auth/utils/postAuthLanding';
import { authClient } from '@/lib/api/authClient';
import { tokenStorage } from '@/lib/auth/tokenStorage';
import { refreshTokens } from '../api/refreshTokens';
import { authResultSchema } from '../schemas/authSchemas';
import { useAuthStore } from '../store/authStore';

// Module-level flag prevents duplicate exchanges across StrictMode remounts.
// useRef resets on remount; module scope does not.
let processingCode: string | null = null;

// Receives the authorization code from the auth service redirect and exchanges
// it for access + refresh tokens. Entire logic is self-contained inside a
// single async IIFE — no useMutation / isSuccess state, so React StrictMode's
// double-mount cannot leave the component in a permanently idle state.
//
// Decision tree:
//   1. Valid session in memory → navigate directly
//   2. No session but refresh token valid → silent refresh → navigate
//   3. Neither → exchange the auth code → navigate
//   4. Exchange fails (code used/expired) → clear + redirect to / for fresh login
export function AuthCallbackScreen() {
  const [params] = useSearchParams();
  const attempted = useRef(false);
  const [msg, setMsg] = useState('Signing you in…');
  const [isErr, setIsErr] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: one-time auth code exchange, must not re-run on params change
  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const code = params.get('code');
    const returnedState = params.get('state');
    const storedState = sessionStorage.getItem(CSRF_KEY);

    if (!code) {
      window.location.assign('/');
      return;
    }

    // StrictMode guard: second mount sees the same code already being processed.
    if (processingCode === code) return;
    processingCode = code;

    void (async () => {
      try {
        // Case 1 — valid session already in Zustand (e.g. Aspire opened an old URL).
        const existingUser = useAuthStore.getState().user;
        if (existingUser) {
          window.location.assign(postAuthLanding(existingUser));
          return;
        }

        // Case 2 — access token missing/expired but refresh token is valid.
        // Recover silently; avoid exchanging an auth code that may be stale.
        if (tokenStorage.getRefreshToken()) {
          const refreshed = await refreshTokens();
          if (refreshed) {
            const freshUser = useAuthStore.getState().user;
            window.location.assign(freshUser ? postAuthLanding(freshUser) : '/dashboard');
            return;
          }
        }

        // Case 3 — exchange the auth code for real tokens.
        // CSRF check (only enforced when a state was stored by ProtectedRoute).
        if (storedState && returnedState !== storedState) {
          useAuthStore.getState().clearSession();
          window.location.assign('/');
          return;
        }
        sessionStorage.removeItem(CSRF_KEY);

        const callbackUrl = `${window.location.origin}/auth/callback`;
        const raw = await authClient
          .post('auth/token', {
            json: { code, redirectUri: callbackUrl, portal: 'studio' },
          })
          .json();

        const result = authResultSchema.parse(raw);
        useAuthStore.getState().setSession(result);

        const freshUser = useAuthStore.getState().user;
        window.location.assign(freshUser ? postAuthLanding(freshUser) : '/dashboard');
      } catch {
        // Case 4 — code invalid/expired or network error → fresh login.
        processingCode = null;
        setIsErr(true);
        setMsg('Session expired. Redirecting to sign in…');
        useAuthStore.getState().clearSession();
        setTimeout(() => window.location.assign('/'), 1500);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex min-h-dvh items-center justify-center">
      <p className={`text-sm ${isErr ? 'text-destructive' : 'text-muted-foreground'}`}>{msg}</p>
    </div>
  );
}
